package com.meistercharts.charts.pv.stringsPlanning

import com.meistercharts.annotations.Window
import com.meistercharts.canvas.MouseCursor
import com.meistercharts.canvas.interaction.HandleRole
import com.meistercharts.canvas.interaction.InteractionState
import com.meistercharts.canvas.interaction.InteractionTarget
import com.meistercharts.canvas.interaction.InteractionTool
import com.meistercharts.canvas.interaction.ToolReaction
import com.meistercharts.charts.pv.roofPlanning.Module
import com.meistercharts.events.EventConsumption
import it.neckar.geometry.Coordinates
import kotlin.uuid.Uuid

/**
 * Wires modules into strings.
 *
 * Pressing a module appends it to the string being planned, or removes it if it is already the string's. Pressing a
 * module of another string switches to that string; pressing next to a module ends the planning.
 */
class StringsPlanningTool : InteractionTool<Module, PvStringsPlanningModel> {

  override val description: String = "Strings planen"

  override fun onHover(
    target: InteractionTarget<Module>?,
    location: @Window Coordinates?,
    state: InteractionState<Module>,
    model: PvStringsPlanningModel,
  ): ToolReaction<Module, PvStringsPlanningModel> {
    //While a string is being planned, the line follows the pointer as long as it is not over a module of a string
    val pendingPoint = when {
      model.selectedString == null -> null
      target == null -> location
      model.findRoofStringContaining(target.element) == null -> location
      else -> null
    }

    return ToolReaction(state.copy(hovered = target, pendingPoint = pendingPoint))
  }

  override fun onPress(
    target: InteractionTarget<Module>?,
    location: @Window Coordinates,
    state: InteractionState<Module>,
    model: PvStringsPlanningModel,
  ): ToolReaction<Module, PvStringsPlanningModel> {
    val module = target?.element
      ?: return endPlanning(state, model)

    val selectedString = model.selectedString
      ?: return startPlanningAt(module, state, model)

    val command = when {
      //Already part of the string being planned - the press takes it out again
      selectedString.contains(module) -> RemoveModuleFromString(selectedString.uuid, module)

      //Part of another string - the press continues that string instead
      else -> when (val otherString = model.findRoofStringContaining(module)) {
        null -> AppendModuleToString(selectedString.uuid, module)
        else -> SelectString(otherString.uuid)
      }
    }

    return ToolReaction(state.copy(hovered = target, pendingPoint = null), listOf(command))
  }

  override fun cursorFor(state: InteractionState<Module>): MouseCursor? {
    return when {
      state.hovered?.role == HandleRole.Body -> MouseCursor.Hand
      state.pendingPoint != null -> MouseCursor.CrossHair
      else -> null
    }
  }

  /**
   * Selects the string that is being planned from outside the canvas - the user picked it in the surrounding UI.
   */
  fun selectString(
    roofStringUuid: Uuid?,
    state: InteractionState<Module>,
    model: PvStringsPlanningModel,
  ): ToolReaction<Module, PvStringsPlanningModel> {
    return ToolReaction(state.copy(pendingPoint = null), listOf(SelectString(roofStringUuid)))
  }

  /**
   * Pressing a module while no string is selected continues the string that module already belongs to.
   */
  private fun startPlanningAt(
    module: Module,
    state: InteractionState<Module>,
    model: PvStringsPlanningModel,
  ): ToolReaction<Module, PvStringsPlanningModel> {
    val roofString = model.findRoofStringContaining(module)
      ?: return ToolReaction(state)

    return ToolReaction(state, listOf(SelectString(roofString.uuid)))
  }

  private fun endPlanning(
    state: InteractionState<Module>,
    model: PvStringsPlanningModel,
  ): ToolReaction<Module, PvStringsPlanningModel> {
    if (model.selectedString == null) {
      return ToolReaction.ignored(state.copy(hovered = null, pendingPoint = null))
    }

    //Ends the planning, but does not consume the event: layers below may still use it
    return ToolReaction(
      state = state.copy(hovered = null, pendingPoint = null),
      commands = listOf(SelectString(null)),
      consumption = EventConsumption.Ignored,
    )
  }
}
